import { useTranslation } from 'react-i18next';
import { ChevronRightIcon } from 'lucide-react';
import { formatBalance } from '@/shared/lib';
import { NUMERIC_CONFIG } from '@/shared/config';
import { LimitProgress } from '@/widgets/user-profile/limits';
import type { LimitRow } from '../model';

type Props = {
  rows: LimitRow[];
  periodEndLabel: string;
  basePrecission?: number;
  baseCode?: string;
  onOpenFull: () => void;
};

const ROW_KEYS: Record<LimitRow['kind'], string> = {
  withdraw: 'personalAccount.balanceDropdown.limits.withdraw',
  deposit: 'personalAccount.balanceDropdown.limits.deposit',
};

export function LimitsSection({
  rows,
  periodEndLabel,
  basePrecission = NUMERIC_CONFIG.precision.default,
  baseCode = '',
  onOpenFull,
}: Props) {
  const { t } = useTranslation();

  return (
    <section className="flex flex-col gap-[0.8rem] px-[1.2rem]">
      <div className="flex items-center gap-[0.4rem]">
        <span className="medium5 text-primary">
          {t('personalAccount.userProfile.limits.title')}
        </span>
        {periodEndLabel && (
          <span className="p6 text-secondary">
            {t('personalAccount.balanceDropdown.limits.until', {
              date: periodEndLabel,
            })}
          </span>
        )}
        <button
          type="button"
          onClick={onOpenFull}
          aria-label={t('personalAccount.balanceDropdown.limits.openFull')}
          className="ml-auto flex items-center justify-center text-tertiary hover:text-primary cursor-pointer transition-colors">
          <ChevronRightIcon className="size-[1.6rem]" />
        </button>
      </div>

      {rows.map(row => (
        <div key={row.kind} className="flex flex-col gap-[0.4rem]">
          <span className="p6 text-secondary truncate">
            {t(ROW_KEYS[row.kind], {
              amount: formatBalance(row.left, basePrecission),
              code: baseCode,
            })}
          </span>
          <LimitProgress
            used={row.used}
            limit={row.limit}
            className="h-[0.4rem] bg-stroke"
            indicatorClassName="bg-tertiary"
          />
        </div>
      ))}
    </section>
  );
}
